import { create } from 'zustand';
import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
} from '@scale/shared/types/chat';
import { MultiTurnThreadHistory } from '../ThreadHistory';
import { useMemo, useCallback } from 'react';
import { ReviewLevel } from '@scale/shared/defaults';
import { setResponseForIndexWithPropagation } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { InlineResponseTemplatingContext } from './InlineResponseTemplatingContext';
import {
  ResponseTemplateVariable,
  ResponseTemplatePopperMetadata,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplateCreatorStep';
import { getStepDescriptorByType } from '../definitions';

type InlineResponseTemplatingStore = {
  variables: ResponseTemplateVariable[];
  setVariables: (variables: ResponseTemplateVariable[]) => void;
  getVariables: () => ResponseTemplateVariable[];
  clear: () => void;
};

const useInlineResponseTemplatingStore = create<InlineResponseTemplatingStore>((set, get) => ({
  variables: [],
  setVariables: (variables: ResponseTemplateVariable[]) => {
    set({ variables });
  },
  getVariables: () => {
    return get().variables;
  },
  clear: () => {
    set({ variables: [] });
  },
}));

export const InlineResponseTemplatingContextProvider = ({
  children,
  templatedResponses,
  setTemplatedResponses,
  config,
  reviewLevel,
  updateResponseMiddleware,
}: {
  children: React.ReactNode;
  config: MultiTurnInteractionConfiguration;
  templatedResponses: IndexedInteractionStepResponse[] | null;
  setTemplatedResponses: (responses: IndexedInteractionStepResponse[]) => void;
  reviewLevel: ReviewLevel;
  updateResponseMiddleware?: (
    response: IndexedInteractionStepResponse,
    metadata: ResponseTemplatePopperMetadata,
    path: string,
    value: any,
    history: MultiTurnThreadHistory,
  ) => void;
}) => {
  const store = useInlineResponseTemplatingStore();

  const history = useMemo(
    () => new MultiTurnThreadHistory(config, templatedResponses ?? [], reviewLevel),
    [config, reviewLevel, templatedResponses],
  );
  const stepsWithResponses = useMemo(() => history.getFlattenedStepsWithResponses(), [history]);

  const setResponseForIndex = useCallback(
    async (response: IndexedInteractionStepResponse, index: number) => {
      const [stepParams] = stepsWithResponses[index];
      if (stepParams) {
        const step = getStepDescriptorByType(stepParams.type);
        // Ensure the response is consistent itself and with previous responses
        step?.ensureResponseConsistency(stepParams, history.until(index), response);
      }

      // Propagate response changes to downstream steps
      const propagatedResponses = setResponseForIndexWithPropagation(
        config,
        response,
        index,
        templatedResponses!,
      );
      setTemplatedResponses(propagatedResponses);
    },
    [stepsWithResponses, history, templatedResponses, setTemplatedResponses, config],
  );

  const updateResponseMiddlewareWrapper = useCallback(
    (
      response: IndexedInteractionStepResponse,
      metadata: ResponseTemplatePopperMetadata,
      path: string,
      value: any,
    ) => {
      updateResponseMiddleware?.(response, metadata, path, value, history);
    },
    [history, updateResponseMiddleware],
  );

  return (
    <InlineResponseTemplatingContext.Provider
      value={{
        templatedResponses,
        setTemplatedResponses,
        history,
        stepsWithResponses,
        setResponseForIndex,
        getVariables: store.getVariables,
        setVariables: store.setVariables,
        clear: store.clear,
        updateResponseMiddleware: updateResponseMiddlewareWrapper,
      }}
    >
      {children}
    </InlineResponseTemplatingContext.Provider>
  );
};
